import {Attachment} from '@hconnect/common/types'
import {customThemeConstants, DeletionModal, LoadingIconButton} from '@hconnect/uikit/src/lib2'
import {Add} from '@mui/icons-material'
import {Box, styled, Grid} from '@mui/material'
import React, {useState, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {AddInformationHeader} from './AddInformationHeader'
import {FilePreview} from './FilePreview'

export interface AttachmentsProps {
  attachedFiles?: Attachment[]
  onUpload?: (files: File[]) => void
  onDelete?: (attachment: Attachment) => void
  isUploading?: boolean
  accept?: string
  inputId?: string
}

const Input = styled('input')({
  display: 'none'
})

const ATTACHMENT_HEIGHT = 120
const ATTACHMENT_WIDTH = 160

const FilePreviews: React.FC<{
  attachedFiles: Attachment[]
  onDeleteFile?: (attachment: Attachment) => void
}> = ({attachedFiles, onDeleteFile}) => (
  <Box mt={2}>
    <Grid container spacing={customThemeConstants().defaultGridSpacing}>
      {attachedFiles.map((attachment) => (
        <Grid item key={attachment.id}>
          <FilePreview
            onRemoveClicked={onDeleteFile}
            height={ATTACHMENT_HEIGHT}
            width={ATTACHMENT_WIDTH}
            attachment={attachment}
          />
        </Grid>
      ))}
    </Grid>
  </Box>
)

export const Attachments: React.FC<AttachmentsProps> = ({
  attachedFiles = [],
  onUpload,
  onDelete,
  isUploading = false,
  accept,
  inputId = 'upload-attachment-input'
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const attachmentToDelete = useRef<Attachment>()
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const onAttachFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    if (fileList && fileList.length > 0) {
      onUpload?.(Array.from(fileList))
    }
    event.target.value = '' // reset the input, so the event is fired if you try to upload the same file again
  }

  const onDeleteFile = onDelete
    ? (attachment: Attachment) => {
        setDialogOpen(true)
        attachmentToDelete.current = attachment
      }
    : undefined

  if (!onUpload && attachedFiles.length === 0) {
    return null
  }

  return (
    <Box my={3}>
      <AddInformationHeader title={t('attachments.heading')}>
        {onUpload && (
          <label htmlFor={inputId}>
            <Input
              disabled={isUploading}
              accept={accept}
              id={inputId}
              multiple
              type="file"
              onChange={onAttachFile}
            />
            <LoadingIconButton
              loading={isUploading}
              data-test-id={'attachments-add-button'}
              component={'span'}
              color={'primary'}
              aria-label={t('attachments.button.ariaLabel')}
            >
              <Add />
            </LoadingIconButton>
          </label>
        )}
      </AddInformationHeader>
      <FilePreviews attachedFiles={attachedFiles} onDeleteFile={onDeleteFile} />
      {onDelete && (
        <DeletionModal
          open={dialogOpen}
          message={t('attachments.deletionDialog.message')}
          cancelButtonCaption={t('button.cancel')}
          cancelButtonTestId={'attachments-delete-cancel-btn'}
          confirmButtonTestId={'attachments-delete-confirm-btn'}
          confirmButtonCaption={t('button.delete')}
          onConfirm={() => {
            if (attachmentToDelete.current) {
              onDelete(attachmentToDelete.current)
            }
            setDialogOpen(false)
          }}
          onCancel={() => {
            setDialogOpen(false)
            attachmentToDelete.current = undefined
          }}
        />
      )}
    </Box>
  )
}
